export interface PropertyPurchased {
    practicePropertyPurchased?: string | null;
    practiceRealPropertyInclude?: string | null;
}



export interface WorkingCapitalHoldback {
    value?: number | null;
    monthsHold?: number | null;
}

export const DEFERRED_FIXED_PAYMENT_COLUMN_LABELS = {
    paymentTiming: 'Payment Timing',
    amount: 'Amount',
    annualizedInterestAccrualRate: 'Annualized Interest Accrual Rate',
    contingent: 'Contingent?',
    contingencyDependentVariable: 'Contingency Dependent Variable ("ContDV")',
    contDvMinimumValue: 'ContDV minimum value',
    employmentContingency: 'Employment Contingency',
    tailPeriodForEmployment: 'Tail Period for Employment',
} as const;

export const DEFERRED_FIXED_PAYMENT_COLUMNS = Object.values(DEFERRED_FIXED_PAYMENT_COLUMN_LABELS);
export const DEFERRED_FIXED_PAYMENT_PERCENTAGE_COLUMNS = [
    DEFERRED_FIXED_PAYMENT_COLUMN_LABELS.annualizedInterestAccrualRate,
];
export const DEFERRED_FIXED_PAYMENT_CURRENCY_COLUMNS = [
    DEFERRED_FIXED_PAYMENT_COLUMN_LABELS.amount,
    DEFERRED_FIXED_PAYMENT_COLUMN_LABELS.contDvMinimumValue,
];

export const DEFERRED_VARIABLE_PAYMENT_COLUMN_LABELS = {
    paymentTiming: 'Payment Timing',
    type: 'Type',
    valueAtClosing: 'Value at Closing',
    calculationDependentVariable: 'Calculation Dependent Variable ("CalcDV")',
    calcDvBaselineValue: 'CalcDV Baseline Value',
    calculationMultiple: 'Calculation Multiple',
    maximumPayment: 'Maximum Payment',
    employmentContingency: 'Employment Contingency',
    tailPeriodForEmploymentContingency: 'Tail period for Employment Contingency',
    penaltyMultiple: 'Penalty Multiple',
    distributionPeriodicity: 'Distribution Periodicity',
} as const;

export const DEFERRED_VARIABLE_PAYMENT_COLUMNS = Object.values(
    DEFERRED_VARIABLE_PAYMENT_COLUMN_LABELS
);
export const DEFERRED_VARIABLE_PAYMENT_CURRENCY_COLUMNS = [
    DEFERRED_VARIABLE_PAYMENT_COLUMN_LABELS.valueAtClosing,
    DEFERRED_VARIABLE_PAYMENT_COLUMN_LABELS.calcDvBaselineValue,
    DEFERRED_VARIABLE_PAYMENT_COLUMN_LABELS.maximumPayment,
];

export const JV_EQUITY_COLUMN_LABELS = {
    valueAtClosing: 'Value at Closing',
    paymentTiming: 'Payment Timing',
    calculationDependentVariable: 'Calculation Dependent Variable ("CalcDV")',
    calcDvBaselineValue: 'CalcDV Baseline Value',
    calculationMultiple: 'Calculation Multiple',
    employmentContingency: 'Employment Contingency',
    tailPeriodForEmploymentContingency: 'Tail period for Employment Contingency',
    penaltyMultiple: 'Penalty Multiple',
    distributionPeriodicity: 'Distribution Periodicity',
} as const;

export const JV_EQUITY_COLUMNS = Object.values(JV_EQUITY_COLUMN_LABELS);
export const JV_EQUITY_CURRENCY_COLUMNS = [
    JV_EQUITY_COLUMN_LABELS.valueAtClosing,
    JV_EQUITY_COLUMN_LABELS.calcDvBaselineValue,
];

export const CORPORATE_EQUITY_COLUMN_LABELS = {
    valueAtClosing: 'Value at Closing',
    expectedTimingToRecap: 'Expected Timing to Recap',
    expectedPayoutAsMultipleOfCurrentValue: 'Expected Payout, as multiple of current value',
} as const;

export const CORPORATE_EQUITY_COLUMNS = Object.values(CORPORATE_EQUITY_COLUMN_LABELS);
export const CORPORATE_EQUITY_CURRENCY_COLUMNS = [
    CORPORATE_EQUITY_COLUMN_LABELS.valueAtClosing,
];

export interface DeferredFixedPaymentRow {
    paymentTiming?: string | null;
    amount?: number | null;
    annualizedInterestAccrualRate?: string | null;
    contingent?: string | null;
    contingencyDependentVariableValue?: string | null;
    contDvMinimumValue?: number | null;
    employmentContingency?: string | null;
    tailPeriodForEmployment?: string | null;
}

export interface DeferredFixedPayments {
    rows: DeferredFixedPaymentRow[];
}

export enum DeferredVariablePaymentType {
    Earnout = 'Earnout',
    Synthetic = 'Synthetic',
}

export interface DeferredVariablePaymentRow {
    paymentTiming?: string | null;
    type?: DeferredVariablePaymentType | null;
    valueAtClosing?: number | null;
    calculationDependentVariable?: string | null;
    calcDvBaselineValue?: number | null;
    calculationMultiple?: string | null;
    maximumPayment?: string | null;
    employmentContingency?: string | null;
    tailPeriodForEmploymentContingency?: string | null;
    penaltyMultiple?: string | null;
    distributionPeriodicity?: string | null;
}

export interface DeferredVariablePayments {
    rows: DeferredVariablePaymentRow[];
}

export interface JvEquityRow {
    valueAtClosing?: number | null;
    paymentTiming?: string | null;
    calculationDependentVariable?: string | null;
    calcDvBaselineValue?: number | null;
    calculationMultiple?: string | null;
    employmentContingency?: string | null;
    tailPeriodForEmploymentContingency?: string | null;
    penaltyMultiple?: string | null;
    distributionPeriodicity?: string | null;
}

export interface JvEquity {
    row?: JvEquityRow | null;
}

export interface CorporateEquityRow {
    valueAtClosing?: number | null;
    expectedTimingToRecap?: string | null;
    expectedPayoutAsMultipleOfCurrentValue?: string | null;
}

export interface CorporateEquity {
    row?: CorporateEquityRow | null;
}

export interface CashAtCloser {
    cashAtClose?: number | null;
    workingCapitalHoldback?: WorkingCapitalHoldback | null;
    netCashAtCloser?: number | null;
}

export interface PurchasePriceSummary {
    cashAtCloser?: CashAtCloser | null;
    deferredFixedPayments?: DeferredFixedPayments | null;
    deferredVariablePayments?: DeferredVariablePayments | null;
    jvEquity?: JvEquity | null;
    corporateEquity?: CorporateEquity | null;
    other?: number | null;
}

export interface NonCompetitionRestriction {
    distanceRadiusMiles?: number | null;
    durationYearsFromClosing?: string | null;
    durationYearsFromEmployment?: string | null;
}

export interface GeneralDeferredPaymentsInformation {
    paymentObligor?: string | null;
    assetsHeldByObligor?: string | null;
    interestAppliedToDeferredPayments?: string | null;
}

export interface LeaseTerms {
    termOfLease?: string | null;
    leaseType?: string | null;
    leaseRate?: string | null;
    leaseRateEscalation?: string | null;
    leaseRateUponExtension?: string | null;
}

export interface TermForm {
    loiExpirationDate?: string | null;
    exclusivityPeriodMonth?: number | null;
    propertyPurchased?: PropertyPurchased | null;
    purchasePriceSummary?: PurchasePriceSummary | null;
    nonCompetitionRestriction?: NonCompetitionRestriction | null;
    generalDeferredPaymentsInformation?: GeneralDeferredPaymentsInformation | null;
    leaseTerms?: LeaseTerms | null;
}

export function createEmptyTermForm(): TermForm {
    return {
        loiExpirationDate: null,
        exclusivityPeriodMonth: null,
        propertyPurchased: {
            practicePropertyPurchased: null,
            practiceRealPropertyInclude: null,
        },
        purchasePriceSummary: {
            cashAtCloser: {
                cashAtClose: null,
                workingCapitalHoldback: { value: null, monthsHold: null },
                netCashAtCloser: null,
            },
            deferredFixedPayments: { rows: [] },
            deferredVariablePayments: { rows: [] },
            jvEquity: { row: null },
            corporateEquity: { row: null },
            other: null,
        },
        nonCompetitionRestriction: {
            distanceRadiusMiles: null,
            durationYearsFromClosing: null,
            durationYearsFromEmployment: null,
        },
        generalDeferredPaymentsInformation: {
            paymentObligor: null,
            assetsHeldByObligor: null,
            interestAppliedToDeferredPayments: null,
        },
        leaseTerms: {
            termOfLease: null,
            leaseType: null,
            leaseRate: null,
            leaseRateEscalation: null,
            leaseRateUponExtension: null,
        },
    };
}
